import api, { formatApiError } from './client';

export const authApi = {
  login: async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    return data;
  },
  logout: async () => {
    const { data } = await api.post('/auth/logout');
    return data;
  },
  getMe: async () => {
    const { data } = await api.get('/auth/me');
    return data;
  },
  refreshToken: async () => {
    const { data } = await api.post('/auth/refresh-token');
    return data;
  },
  forgotPassword: async (email) => {
    const { data } = await api.post('/auth/forgot-password', { email });
    return data;
  },
  resetPassword: async (token, newPassword) => {
    const { data } = await api.post('/auth/reset-password', { token, new_password: newPassword });
    return data;
  },
};

export const userApi = {
  list: async (params = {}) => {
    const { data } = await api.get('/users', { params });
    return data;
  },
  get: async (id) => {
    const { data } = await api.get(`/users/${id}`);
    return data;
  },
  create: async (userData) => {
    const { data } = await api.post('/users', userData);
    return data;
  },
  update: async (id, userData) => {
    const { data } = await api.put(`/users/${id}`, userData);
    return data;
  },
  deactivate: async (id) => {
    const { data } = await api.patch(`/users/${id}/deactivate`);
    return data;
  },
  activate: async (id) => {
    const { data } = await api.patch(`/users/${id}/activate`);
    return data;
  },
  resetPassword: async (id) => {
    const { data } = await api.patch(`/users/${id}/reset-password`);
    return data;
  },
};

export const roleApi = {
  list: async () => {
    const { data } = await api.get('/roles');
    return data;
  },
  get: async (id) => {
    const { data } = await api.get(`/roles/${id}`);
    return data;
  },
  create: async (roleData) => {
    const { data } = await api.post('/roles', roleData);
    return data;
  },
  update: async (id, roleData) => {
    const { data } = await api.put(`/roles/${id}`, roleData);
    return data;
  },
  delete: async (id) => {
    const { data } = await api.delete(`/roles/${id}`);
    return data;
  },
  getPermissions: async (roleId) => {
    const { data } = await api.get(`/permissions/role/${roleId}`);
    return data;
  },
  updatePermissions: async (roleId, permissions) => {
    const { data } = await api.put(`/permissions/role/${roleId}`, { permissions });
    return data;
  },
};

export const moduleApi = {
  list: async () => {
    const { data } = await api.get('/modules');
    return data;
  },
};

export const auditApi = {
  list: async (params = {}) => {
    const { data } = await api.get('/audit-logs', { params });
    return data;
  },
  exportAll: async (params = {}) => {
    const { data } = await api.get('/audit-logs/export', { params });
    return data;
  },
  exportExcel: async (params = {}) => {
    const response = await api.get('/audit-logs/export-excel', { 
      params,
      responseType: 'blob'
    });
    return response;
  },
};

export const dashboardApi = {
  getStats: async () => {
    const { data } = await api.get('/dashboard/stats');
    return data;
  },
  // P10.6 — overdue PO line deliveries
  lateDeliveries: async () => {
    const { data } = await api.get('/dashboard/late-deliveries');
    return data;
  },
  // P11.5 — PRCs with no recent activity
  stalePrcs: async (days = 14) => {
    const { data } = await api.get('/dashboard/stale-prcs', { params: { days } });
    return data;
  },
  // Phase D / Parcel D3 — 7 aggregation tiles
  rmStock:          async () => (await api.get('/dashboard/rm-stock')).data,
  reorderAlerts:    async () => (await api.get('/dashboard/reorder-alerts')).data,
  productionStatus: async () => (await api.get('/dashboard/production-status')).data,
  dispatchStatus:   async () => (await api.get('/dashboard/dispatch-status')).data,
  qualitySummary:   async () => (await api.get('/dashboard/quality-summary')).data,
  ncSummary:        async () => (await api.get('/dashboard/nc-summary')).data,
  planVsActual:     async () => (await api.get('/dashboard/plan-vs-actual')).data,
  planVsDispatch:   async () => (await api.get('/dashboard/plan-vs-dispatch')).data,
  // Phase 6 — extras
  getCustomerOrderStatus: async () => (await api.get('/dashboard/customer-order-status')).data,
  getFgAvailability:      async () => (await api.get('/dashboard/fg-availability')).data,
  productionOverview:     async (params = {}) => (await api.get('/dashboard/production-overview', { params })).data,
};

// ========== Traceability ==========
export const traceabilityApi = {
  lookup: async (type, value) => {
    const { data } = await api.get('/traceability/lookup', { params: { type, value } });
    return data;
  },
};

// ============ PHASE 2 APIs ============

export const settingsApi = {
  getCompanyProfile: async () => {
    const { data } = await api.get('/settings/company-profile');
    return data;
  },
  updateCompanyProfile: async (profileData) => {
    const { data } = await api.put('/settings/company-profile', profileData);
    return data;
  },
  getDocumentFormats: async () => {
    const { data } = await api.get('/settings/document-formats');
    return data;
  },
  updateDocumentFormats: async (formats) => {
    const { data } = await api.put('/settings/document-formats', formats);
    return data;
  },
  resetSequence: async (docType) => {
    const { data } = await api.post(`/settings/document-formats/${docType}/reset-sequence`);
    return data;
  },
  getQRConfig: async () => {
    const { data } = await api.get('/settings/qr-label-config');
    return data;
  },
  updateQRConfig: async (config) => {
    const { data } = await api.put('/settings/qr-label-config', config);
    return data;
  },
};

export const masterApi = {
  getSummary: async () => {
    const { data } = await api.get('/masters/summary');
    return data;
  },
  list: async (type, params = {}) => {
    const { data } = await api.get(`/masters/${type}`, { params });
    return data;
  },
  get: async (type, id) => {
    const { data } = await api.get(`/masters/${type}/${id}`);
    return data;
  },
  create: async (type, itemData) => {
    const { data } = await api.post(`/masters/${type}`, itemData);
    return data;
  },
  update: async (type, id, itemData) => {
    const { data } = await api.put(`/masters/${type}/${id}`, itemData);
    return data;
  },
  toggleStatus: async (type, id) => {
    const { data } = await api.patch(`/masters/${type}/${id}/toggle-status`);
    return data;
  },
  getGradeTDS: async (gradeId) => {
    const { data } = await api.get(`/masters/grades/${gradeId}/tds`);
    return data;
  },
  saveGradeTDS: async (gradeId, tdsData) => {
    const { data } = await api.post(`/masters/grades/${gradeId}/tds`, tdsData);
    return data;
  },
  getGradeTDSHistory: async (gradeId) => {
    const { data } = await api.get(`/masters/grades/${gradeId}/tds/history`);
    return data;
  },
  uploadDocument: async (file, onProgress) => {
    const fd = new FormData();
    fd.append('file', file);
    const { data } = await api.post('/masters/documents/upload', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress,
    });
    return data;
  },
};

// Phase 2 v3 — RM Master composite
export const rmMasterApi = {
  list: async (params = {}) => (await api.get('/rm-master', { params })).data,
  get: async (id) => (await api.get(`/rm-master/${id}`)).data,
  create: async (payload) => (await api.post('/rm-master', payload)).data,
  update: async (id, payload) => (await api.put(`/rm-master/${id}`, payload)).data,
  toggleStatus: async (id) => (await api.patch(`/rm-master/${id}/toggle-status`)).data,
  previewCode: async (supplierId) => (await api.get('/rm-master/preview-code', { params: { supplier_id: supplierId } })).data,
  dropdown: async (params = {}) => (await api.get('/rm-master/dropdown', { params })).data,
  // D1.2 — View Details drawer payload: reorder_uom, stock_qty, recent_grns
  details: async (id) => (await api.get(`/rm-master/${id}/details`)).data,
  // Phase 1 — new detail sub-resources
  qualityHistory: async (id, limit = 20) => (await api.get(`/rm-master/${id}/quality-history`, { params: { limit } })).data,
  transactions:   async (id, limit = 20) => (await api.get(`/rm-master/${id}/transactions`,   { params: { limit } })).data,
};

// Phase 1 — Supplier detail sub-resources
export const supplierApi = {
  summary:         async (id) => (await api.get(`/masters/suppliers/${id}/summary`)).data,
  purchaseHistory: async (id, limit = 50) => (await api.get(`/masters/suppliers/${id}/purchase-history`, { params: { limit } })).data,
};

// Phase 1 — Customer delivery locations convenience helpers
export const customerApi = {
  deliveryLocations: async (customerId, activeOnly = true) =>
    (await api.get(`/masters/customers/${customerId}/delivery-locations`, { params: { active_only: activeOnly } })).data,
};

// Phase 2 v3 — FG Master composite
export const fgMasterApi = {
  list: async (params = {}) => (await api.get('/fg-master', { params })).data,
  get: async (id) => (await api.get(`/fg-master/${id}`)).data,
  create: async (payload) => (await api.post('/fg-master', payload)).data,
  update: async (id, payload) => (await api.put(`/fg-master/${id}`, payload)).data,
  toggleStatus: async (id) => (await api.patch(`/fg-master/${id}/toggle-status`)).data,
  previewCode: async (customerId) => (await api.get('/fg-master/preview-code', { params: { customer_id: customerId } })).data,
  dropdown: async (params = {}) => (await api.get('/fg-master/dropdown', { params })).data,
  // Auth-only (no `master_management:view` gate) — used by the Planning-run
  // edit screen to pre-fill FG chips as the Planner role, which cannot call
  // the gated `fgMasterApi.get(id)`.
  dropdownOne: async (fgId) => (await api.get(`/fg-master/dropdown/${fgId}`)).data,
  // P2.4 / P2.5 — Generate PRC from FG and fetch denormalised context for downstream forms
  generatePrc: async (id) => (await api.post(`/fg-master/${id}/generate-prc`)).data,
  context: async (id) => (await api.get(`/fg-master/${id}/context`)).data,
  // G1.2 — FG ↔ PRC mapping helpers
  patchMapping: async (id, prcId) => (await api.patch(`/fg-master/${id}`, { prc_id: prcId })).data,
  getMappedPrc: async (id) => (await api.get(`/fg-master/${id}/prc`)).data,
  bulkMap: async (rows) => (await api.post('/fg-master/bulk-map', rows)).data,
  // G2.2 — Single-shot payload consumed by the SO create page.
  getSoContext: async (id) => (await api.get(`/fg-master/${id}/so-context`)).data,
  // Feb 2026 Q13 — Optional FG approval workflow.
  submitForApproval: async (id) => (await api.post(`/fg-master/${id}/submit-for-approval`)).data,
  approve: async (id) => (await api.post(`/fg-master/${id}/approve`)).data,
  reject: async (id, remarks) => (await api.post(`/fg-master/${id}/reject`, { remarks })).data,
  // Feb 2026 Q14 — PRC history per FG.
  prcHistory: async (id) => (await api.get(`/fg-master/${id}/prc-history`)).data,
};

export const purchaseOrderApi = {
  list: async (params = {}) => {
    const { data } = await api.get('/purchase-orders', { params });
    return data;
  },
  get: async (id) => {
    const { data } = await api.get(`/purchase-orders/${id}`);
    return data;
  },
  create: async (poData) => {
    const { data } = await api.post('/purchase-orders', poData);
    return data;
  },
  update: async (id, poData) => {
    const { data } = await api.put(`/purchase-orders/${id}`, poData);
    return data;
  },
  approve: async (id) => {
    const { data } = await api.patch(`/purchase-orders/${id}/approve`);
    return data;
  },
  shortClose: async (id, reason) => (await api.patch(`/purchase-orders/${id}/short-close`, { reason })).data,
  cancel: async (id, reason = '') => {
    const { data } = await api.patch(`/purchase-orders/${id}/cancel?reason=${encodeURIComponent(reason)}`);
    return data;
  },
  dropdown: async (params = {}) => {
    const { data } = await api.get('/purchase-orders/dropdown', { params });
    return data;
  },
  priceHint: async (gradeId, sizeId, supplierId) => {
    const { data } = await api.get('/purchase-orders/price-hint', { params: { grade_id: gradeId, size_id: sizeId, supplier_id: supplierId } });
    return data;
  },
  linked: async (id) => {
    const { data } = await api.get(`/purchase-orders/${id}/linked`);
    return data;
  },
};

export const grnApi = {
  list: async (params = {}) => {
    const { data } = await api.get('/grn', { params });
    return data;
  },
  get: async (id) => {
    const { data } = await api.get(`/grn/${id}`);
    return data;
  },
  create: async (grnData) => {
    const { data } = await api.post('/grn', grnData);
    return data;
  },
  confirm: async (id) => {
    const { data } = await api.patch(`/grn/${id}/confirm`);
    return data;
  },
  updateHeader: async (id, body) => (await api.put(`/grn/${id}`, body)).data,
  updateLine: async (lineId, body) => (await api.put(`/grn/lines/${lineId}`, body)).data,
  discard: async (id, reason) => (await api.patch(`/grn/${id}/discard`, { reason })).data,
  reverse: async (id, reason) => (await api.patch(`/grn/${id}/reverse`, { reason })).data,
  getLabels: async (id) => {
    const { data } = await api.get(`/grn/${id}/labels`);
    return data;
  },
  markLabelPrinted: async (lineId) => {
    const { data } = await api.patch(`/grn/lines/${lineId}/print-label`);
    return data;
  },
  getPendingPOLines: async (poId) => {
    const { data } = await api.get(`/grn/pending-po-lines/${poId}`);
    return data;
  },
  exportExcel: async (grnId) => {
    const response = await api.get(`/grn/${grnId}/export-excel`, { 
      responseType: 'blob'
    });
    return response;
  },
  track: async (grnId) => {
    const { data } = await api.get(`/track/${grnId}`);
    return data;
  },
};

export const grnCoilApi = {
  list: async (grnId) => {
    const { data } = await api.get(`/grn/${grnId}/coils`);
    return data;
  },
  bulkCreate: async (grnId, lineId, coils) => {
    const { data } = await api.post(`/grn/${grnId}/lines/${lineId}/coils`, coils);
    return data;
  },
  // Generate the declared coil rows for a line that has none.
  autoSeed: async (grnId, lineId) => {
    const { data } = await api.post(`/grn/${grnId}/lines/${lineId}/coils/auto`);
    return data;
  },
  update: async (coilId, payload) => {
    const { data } = await api.put(`/grn/coils/${coilId}`, payload);
    return data;
  },
  remove: async (coilId) => {
    const { data } = await api.delete(`/grn/coils/${coilId}`);
    return data;
  },
  printLabel: async (coilId) => {
    const { data } = await api.post(`/grn/coils/${coilId}/label/print`);
    return data;
  },
  // G4.4 — bulk coil-label PDF for a GRN (comma-separated coil ids).
  batchLabelPdfUrl: (coilIds) => {
    const list = Array.isArray(coilIds) ? coilIds.join(',') : coilIds;
    return `${process.env.REACT_APP_BACKEND_URL}/api/grn/coils-batch/label-pdf?ids=${encodeURIComponent(list)}`;
  },
};

export const annealingPlanApi = {
  // PRCs past Draft that hold allotted SC: the only ones a plan can be made on.
  prcsWithSc: async (search) => (await api.get('/annealing/prcs-with-sc', { params: { search: search || undefined } })).data,
  list: async (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v !== '' && v != null) params.append(k, v); });
    const { data } = await api.get(`/annealing/plans?${params.toString()}`);
    return data;
  },
  headers: async (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v !== '' && v != null) params.append(k, v); });
    const { data } = await api.get(`/annealing/plans/headers?${params.toString()}`);
    return data;
  },
  create: async (payload) => {
    const { data } = await api.post('/annealing/plans', payload);
    return data;
  },
  detail: async (planId) => {
    const { data } = await api.get(`/annealing/plans/${planId}`);
    return data;
  },
  availableCoils: async (planId, filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v !== '' && v != null) params.append(k, v); });
    const { data } = await api.get(`/annealing/plans/${planId}/available-coils?${params.toString()}`);
    return data;
  },
  addCoils: async (planId, grn_coil_ids) => {
    const { data } = await api.post(`/annealing/plans/${planId}/coils`, { grn_coil_ids });
    return data;
  },
  removeCoil: async (planId, planCoilId) => {
    const { data } = await api.delete(`/annealing/plans/${planId}/coils/${planCoilId}`);
    return data;
  },
  updateCoil: async (planId, planCoilId, body) => {
    const { data } = await api.patch(`/annealing/plans/${planId}/coils/${planCoilId}`, body);
    return data;
  },
  markOutput: async (planId, coilOutputs) => {
    const { data } = await api.post(`/annealing/plans/${planId}/mark-output`, { coil_outputs: coilOutputs });
    return data;
  },
  changeStatus: async (planId, production_status) => {
    const { data } = await api.patch(`/annealing/plans/${planId}/status`, { production_status });
    return data;
  },
  updateHeader: async (planId, body) => {
    const { data } = await api.patch(`/annealing/plans/${planId}`, body);
    return data;
  },
  prcContext: async (prcId) => {
    const { data } = await api.get(`/annealing/prc-context/${prcId}`);
    return data;
  },
};

// ========== Furnace Master ==========
export const furnaceApi = {
  list: async (activeOnly = false) => {
    const { data } = await api.get('/masters/furnaces', { params: { active_only: activeOnly } });
    return data;
  },
  create: async (payload) => {
    const { data } = await api.post('/masters/furnaces', payload);
    return data;
  },
  update: async (id, payload) => {
    const { data } = await api.put(`/masters/furnaces/${id}`, payload);
    return data;
  },
  remove: async (id) => {
    const { data } = await api.delete(`/masters/furnaces/${id}`);
    return data;
  },
  processTypes: async () => {
    const { data } = await api.get('/masters/furnaces/process-types');
    return data;
  },
};

export const reportsApi = {
  exportStockCodeRegister: async (params = {}) => {
    const response = await api.get('/reports/stock-code-register/export', {
      params,
      responseType: 'blob'
    });
    return response;
  },
  exportBaywiseRegister: async (params = {}) => {
    const response = await api.get('/reports/baywise-register/export', {
      params,
      responseType: 'blob'
    });
    return response;
  },
  exportSupplierPerformance: async (params = {}) => {
    const response = await api.get('/reports/supplier-performance/export', {
      params,
      responseType: 'blob'
    });
    return response;
  },
};

// ============ EXPORT APIs ============

export const exportApi = {
  rmTds: (gradeId) => `${api.defaults.baseURL}/exports/rm-tds/${gradeId}`,
  purchaseOrder: (poId) => `${api.defaults.baseURL}/exports/purchase-order/${poId}`,
  salesModule: (moduleType) => `${api.defaults.baseURL}/exports/sales/${moduleType}`,
  productionModule: (moduleType) => `${api.defaults.baseURL}/exports/production/${moduleType}`,
  inquiryRegister: () => `${api.defaults.baseURL}/inquiries/register/excel`,
  feasibilityReport: (id) => `${api.defaults.baseURL}/exports/mkt/feasibility/${id}`,
  cioa: (id) => `${api.defaults.baseURL}/exports/mkt/cioa/${id}`,
  pda: (id) => `${api.defaults.baseURL}/exports/mkt/pda/${id}`,
  costing: (id) => `${api.defaults.baseURL}/exports/mkt/costing/${id}`,
  paymentRegister: () => `${api.defaults.baseURL}/exports/mkt/payment-register`,
  complaintRegister: () => `${api.defaults.baseURL}/exports/mkt/complaint-register`,
};

// ============ SALES & MARKETING APIs ============

export const salesApi = {
  summary: async () => {
    const { data } = await api.get('/sales/summary');
    return data;
  },
  list: async (moduleType, params = {}) => {
    const { data } = await api.get(`/sales/${moduleType}`, { params });
    return data;
  },
  get: async (moduleType, id) => {
    const { data } = await api.get(`/sales/${moduleType}/${id}`);
    return data;
  },
  create: async (moduleType, itemData) => {
    const { data } = await api.post(`/sales/${moduleType}`, itemData);
    return data;
  },
  update: async (moduleType, id, itemData) => {
    const { data } = await api.put(`/sales/${moduleType}/${id}`, itemData);
    return data;
  },
  delete: async (moduleType, id) => {
    const { data } = await api.delete(`/sales/${moduleType}/${id}`);
    return data;
  },
  dropdownInquiries: async () => {
    const { data } = await api.get('/sales/dropdown/inquiries');
    return data;
  },
  dropdownQuotations: async () => {
    const { data } = await api.get('/sales/dropdown/quotations');
    return data;
  },
  dropdownSalesOrders: async () => {
    const { data } = await api.get('/sales/dropdown/sales-orders');
    return data;
  },
  changeSOStatus: async (soId, status, remarks) => {
    const { data } = await api.patch(`/sales/sales-orders/${soId}/status`, { status, remarks });
    return data;
  },
};

// ============ PRODUCTION MANAGEMENT APIs ============

export const productionApi = {
  summary: async () => {
    const { data } = await api.get('/production/summary');
    return data;
  },
  list: async (moduleType, params = {}) => {
    const { data } = await api.get(`/production/${moduleType}`, { params });
    return data;
  },
  get: async (moduleType, id) => {
    const { data } = await api.get(`/production/${moduleType}/${id}`);
    return data;
  },
  create: async (moduleType, itemData) => {
    const { data } = await api.post(`/production/${moduleType}`, itemData);
    return data;
  },
  update: async (moduleType, id, itemData) => {
    const { data } = await api.put(`/production/${moduleType}/${id}`, itemData);
    return data;
  },
  delete: async (moduleType, id) => {
    const { data } = await api.delete(`/production/${moduleType}/${id}`);
    return data;
  },
  // Phase: PRC Master — Printable PDF (download / open in new tab)
  prcPrintUrl: (id) => `${process.env.REACT_APP_BACKEND_URL}/api/production/prc/${id}/print`,
  prcDownload: async (id) => {
    const resp = await api.get(`/production/prc/${id}/print`, { responseType: 'blob' });
    return resp.data; // Blob
  },
};

// ============ OPERATIONS APIs (Inspection, Storage, Dispatch) ============

export const inspectionApi = {
  list: async (params = {}) => (await api.get('/inspection', { params })).data,
  get: async (id) => (await api.get(`/inspection/${id}/full`)).data,
  startInspection: async (grnLineId) => (await api.post(`/inspection/${grnLineId}/start`)).data,
  beginTesting: async (id) => (await api.patch(`/inspection/${id}/testing-started`)).data,
  saveChemical: async (id, payload) => (await api.patch(`/inspection/${id}/chemical`, payload)).data,
  saveMechanical: async (id, payload) => (await api.patch(`/inspection/${id}/mechanical`, payload)).data,
  saveDimensional: async (id, payload) => (await api.patch(`/inspection/${id}/dimensional`, payload)).data,
  saveVisual: async (id, payload) => (await api.patch(`/inspection/${id}/visual`, payload)).data,
  submitToLab: async (id) => (await api.patch(`/inspection/${id}/lab-verification`)).data,
  decide: async (id, payload) => (await api.patch(`/inspection/${id}/decision`, payload)).data,
  // Feb 2026 Issue #6 (recurrence) — Do NOT pass an explicit Content-Type here.
  // Setting `multipart/form-data` manually strips the boundary axios/fetch
  // generates for FormData bodies, so the server sees a malformed part and
  // rejects the upload. Letting axios detect the FormData auto-adds the
  // correct boundary.
  // Aug 2026 (recurrence #2) — BELT-AND-BRACES: explicitly clear the axios
  // default `Content-Type: application/json` header on this multipart call.
  // Some axios build variants preserve the default header for FormData bodies
  // which corrupts the request. Setting `undefined` here forces the browser
  // to compute the correct `multipart/form-data; boundary=...` header.
  uploadMtc: async (inspId, formData) => (await api.post(
    `/inspection/${inspId}/mtc`,
    formData,
    { headers: { 'Content-Type': undefined } },
  )).data,
  getMtcFiles: async (inspId) => (await api.get(`/inspection/${inspId}/mtc`)).data,
  deleteMtc: async (inspId, fileId) => (await api.delete(`/inspection/${inspId}/mtc/${fileId}`)).data,
  exportPdf: async (id) => api.get(`/inspection/${id}/pdf`, { responseType: 'blob' }),
};

export const operationsApi = {
  inspectionQueue: async (params = {}) => {
    const { data } = await api.get('/inspection/queue', { params });
    return data;
  },
  startInspection: async (id) => {
    const { data } = await api.patch(`/inspection/${id}/start`);
    return data;
  },
  completeInspection: async (id, payload) => {
    const { data } = await api.patch(`/inspection/${id}/complete`, payload);
    return data;
  },
  getInspection: async (id) => {
    const { data } = await api.get(`/inspection/${id}`);
    return data;
  },
  updateInspection: async (id, payload) => {
    const { data } = await api.put(`/inspection/${id}`, payload);
    return data;
  },
  deleteInspection: async (id) => {
    const { data } = await api.delete(`/inspection/${id}`);
    return data;
  },
  storageInventory: async (params = {}) => {
    const { data } = await api.get('/storage/inventory', { params });
    return data;
  },
  warehouses: async () => {
    const { data } = await api.get('/storage/warehouses');
    return data;
  },
  listBins: async (params = {}) => {
    const { data } = await api.get('/storage/bins', { params });
    return data;
  },
  createBin: async (payload) => {
    const { data } = await api.post('/storage/bins', payload);
    return data;
  },
  putaway: async (grnLineId, allocations) =>
    (await api.post('/storage/putaway', { grn_line_id: grnLineId, allocations })).data,
  dispatchReady: async (params = {}) => {
    const { data } = await api.get('/dispatch/ready', { params });
    return data;
  },
  listDispatched: async (params = {}) => {
    const { data } = await api.get('/dispatch', { params });
    return data;
  },
  advancedDashboard: async () => {
    const { data } = await api.get('/dashboard/advanced');
    return data;
  },
};

export { formatApiError };

// ========== Notifications ==========
export const notificationsApi = {
  list: async ({ unread_only = false, limit = 50, offset = 0 } = {}) => {
    const { data } = await api.get('/notifications', { params: { unread_only, limit, offset } });
    return data;
  },
  unreadCount: async () => {
    const { data } = await api.get('/notifications/unread-count');
    return data;
  },
  markRead: async (id) => {
    const { data } = await api.post(`/notifications/${id}/read`);
    return data;
  },
  markAllRead: async () => {
    const { data } = await api.post('/notifications/mark-all-read');
    return data;
  },
  dismiss: async (id) => {
    const { data } = await api.delete(`/notifications/${id}`);
    return data;
  },
  clearRead: async () => {
    const { data } = await api.post('/notifications/clear-all');
    return data;
  },
};

// ========== Lab Quality ==========
export const labQualityApi = {
  listRm: async (params = {}) => (await api.get('/lab-quality/rm-inspection', { params })).data,
  createRm: async (payload) => (await api.post('/lab-quality/rm-inspection', payload)).data,
  updateRm: async (id, payload) => (await api.put(`/lab-quality/rm-inspection/${id}`, payload)).data,
  listFurnace: async (params = {}) => (await api.get('/lab-quality/furnace-inspection', { params })).data,
  createFurnace: async (payload) => (await api.post('/lab-quality/furnace-inspection', payload)).data,
  updateFurnace: async (id, payload) => (await api.put(`/lab-quality/furnace-inspection/${id}`, payload)).data,
  // Stage 1 keys off SC No. — these back the SC picker + GRN header autofill.
  scOptions: async (search, limit = 10) =>
    (await api.get('/lab-quality/sc-options', { params: { search: search || '', limit } })).data,
  scHeader: async (sc) =>
    (await api.get(`/lab-quality/sc-header/${encodeURIComponent(sc)}`)).data,
  listMicro: async (params = {}) => (await api.get('/lab-quality/micro-hardness', { params })).data,
  createMicro: async (payload) => (await api.post('/lab-quality/micro-hardness', payload)).data,
  listMechanical: async (params = {}) => (await api.get('/lab-quality/mechanical-properties', { params })).data,
  getMechanical: async (id) => (await api.get(`/lab-quality/mechanical-properties/${id}`)).data,
  createMechanical: async (payload) => (await api.post('/lab-quality/mechanical-properties', payload)).data,
  approveMechanical: async (id, level) =>
    (await api.post(`/lab-quality/mechanical-properties/${id}/approve`, { level })).data,
  rejectMechanical: async (id, reason) =>
    (await api.post(`/lab-quality/mechanical-properties/${id}/reject`, { reason })).data,
  listTc: async (params = {}) => (await api.get('/lab-quality/test-certificates', { params })).data,
  createTc: async (payload) => (await api.post('/lab-quality/test-certificates', payload)).data,
  getTc: async (id) => (await api.get(`/lab-quality/test-certificates/${id}`)).data,
  updateTc: async (id, payload) => (await api.put(`/lab-quality/test-certificates/${id}`, payload)).data,
  tcPrefill: async (sc) => (await api.get('/lab-quality/test-certificates/prefill', { params: { sc_number: sc } })).data,
  tcFormOptions: async () => (await api.get('/lab-quality/test-certificates/form-options')).data,
  trace: async (heat_code) => (await api.get(`/lab-quality/traceability/${encodeURIComponent(heat_code)}`)).data,
  heatSuggestions: async (q, limit = 20) =>
    (await api.get('/lab-quality/heat-code-suggestions', { params: { q: q || '', limit } })).data,
  bulkImportRm: async (rows) =>
    (await api.post('/lab-quality/bulk-import/rm-inspection', { rows })).data,
  heatCoverPdfUrl: (heat_code) =>
    `${process.env.REACT_APP_BACKEND_URL}/api/exports/lab-quality/heat-cover/${encodeURIComponent(heat_code)}`,
  // The endpoint reads the session header, not ?t= (the token rode in the URL for nothing).
  openHeatCoverPdf: async (heat_code) => {
    const r = await api.get(`/exports/lab-quality/heat-cover/${encodeURIComponent(heat_code)}`, { responseType: 'blob' });
    const url = URL.createObjectURL(r.data);
    window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  },
};

// ========== Analytics ==========
export const analyticsApi = {
  vendorRating: async (months = 6, limit = 50) =>
    (await api.get('/analytics/vendor-rating', { params: { months, limit } })).data,
  rejectionAnomalies: async (days = 30) =>
    (await api.get('/analytics/rejection-anomalies', { params: { days } })).data,
};

// ========== RM Issue & Dispatch ==========
export const rmIssueApi = {
  list: async (params = {}) => (await api.get('/rm-issue', { params })).data,
  get: async (id) => (await api.get(`/rm-issue/${id}`)).data,
  create: async (payload) => (await api.post('/rm-issue', payload)).data,
  update: async (id, payload) => (await api.patch(`/rm-issue/${id}`, payload)).data,
  // Bounded: a confirm that the server never answers (a DB lock wait, a proxy
  // stall) used to leave the button spinning forever with nothing on screen.
  confirm: async (id) => (await api.patch(`/rm-issue/${id}/confirm`, null, { timeout: 90_000 })).data,
  complete: async (id) => (await api.patch(`/rm-issue/${id}/complete`, null, { timeout: 90_000 })).data,
  // Source: masters/units (only active units).
  units: async () => (await api.get('/masters/units', { params: { limit: 100 } })).data,
  // Phase 2 — stock code picker
  searchStockCodes: async (params = {}) => (await api.get('/rm-issue/available-stock-codes', { params })).data,
  getAvailableStock: async (params = {}) => (await api.get('/rm-issue/stock/available', { params })).data,
  // Phase 2 — PRC / SC pickers
  searchPrc: async (params = {}) => (await api.get('/rm-issue/prc-search', { params })).data,
  getPrcScNumbers: async (prcId) => (await api.get(`/rm-issue/prc/${prcId}/sc-numbers`)).data,
  // Phase 2b — per-line auto-populate lookup (FG or PRC master fallback)
  prcLineLookup: async (params = {}) => (await api.get('/rm-issue/prc-line-lookup', { params })).data,
  cancel: async (id, reason) => (await api.post(`/rm-issue/${id}/cancel`, { reason })).data,
};

// Phase 2c — Warehouse-scoped Bay master (label: "Bay", table: bin_master)
export const bayApi = {
  list: async (warehouseId, params = {}) =>
    (await api.get(`/masters/warehouses/${warehouseId}/bays`, { params })).data,
  create: async (warehouseId, body) =>
    (await api.post(`/masters/warehouses/${warehouseId}/bays`, body)).data,
  bulkAdd: async (warehouseId, bays) =>
    (await api.post(`/masters/warehouses/${warehouseId}/bays/bulk`, { bays })).data,
  update: async (warehouseId, bayId, body) =>
    (await api.patch(`/masters/warehouses/${warehouseId}/bays/${bayId}`, body)).data,
  remove: async (warehouseId, bayId) =>
    (await api.delete(`/masters/warehouses/${warehouseId}/bays/${bayId}`)).data,
};

// Phase 2d — Blocked Stock module
export const blockedStockApi = {
  list: async (params = {}) => (await api.get('/blocked-stock', { params })).data,
  create: async (body) => (await api.post('/blocked-stock', body)).data,
  get: async (id) => (await api.get(`/blocked-stock/${id}`)).data,
  listRemarks: async (id) => (await api.get(`/blocked-stock/${id}/remarks`)).data,
  addRemark: async (id, remark_text) =>
    (await api.post(`/blocked-stock/${id}/remarks`, { remark_text })).data,
  unblock: async (id, unblock_remarks) =>
    (await api.patch(`/blocked-stock/${id}/unblock`, { unblock_remarks })).data,
  traceability: async (id) => (await api.get(`/blocked-stock/${id}/traceability`)).data,
  // Deviation flow (Phase 3)
  requestDeviation: async (id, body) => (await api.post(`/blocked-stock/${id}/request-deviation`, body)).data,
  hodVerify: async (id, body) => (await api.patch(`/blocked-stock/${id}/hod-verify`, body)).data,
  directorApprove: async (id, body) => (await api.patch(`/blocked-stock/${id}/director-approve`, body)).data,
  returnToBin: async (id, body) => (await api.patch(`/blocked-stock/${id}/return-to-bin`, body)).data,
  // Scrap / Rework (Phase 5)
  scrap: async (id, body) => (await api.post(`/blocked-stock/${id}/scrap`, body)).data,
  rework: async (id, body) => (await api.post(`/blocked-stock/${id}/rework`, body)).data,
};

// Phase 6 — FG Process Loss
export const fgProcessLossApi = {
  reasons: async () => (await api.get('/fg-process-loss/reasons')).data,
  post: async (fg_inv_id, body) => (await api.post(`/fg-process-loss/${fg_inv_id}`, body)).data,
  approve: async (loss_id, body) => (await api.patch(`/fg-process-loss/${loss_id}/approve`, body)).data,
  history: async (fg_inv_id) => (await api.get(`/fg-process-loss/${fg_inv_id}/history`)).data,
};

// Phase 2 — User Preferences (per-user, JSON values)
export const preferencesApi = {
  list: async () => (await api.get('/preferences/me')).data,
  put: async (key, value) => (await api.put(`/preferences/me/${encodeURIComponent(key)}`, { value })).data,
  remove: async (key) => (await api.delete(`/preferences/me/${encodeURIComponent(key)}`)).data,
};

// Phase 2 — generic approval engine (configurable per module)
export const approvalEngineApi = {
  config: async () => (await api.get('/approval-engine/_config')).data,
  approve: async (module, recordId, level) => (await api.post(`/approval-engine/${module}/${recordId}/approve`, { level })).data,
  reject: async (module, recordId, reason) => (await api.post(`/approval-engine/${module}/${recordId}/reject`, { reason })).data,
};

// Phase 2 — server-side PDF renderer (Playwright)
export const reportRendererApi = {
  health: async () => (await api.get('/reports/_health')).data,
  pdfUrl: (reportType, recordId, layout = 'jina') =>
    `${api.defaults.baseURL}/reports/${reportType}/${recordId}/pdf?layout=${encodeURIComponent(layout)}`,
};

// ========== Role Bulk Assign ==========
export const roleBulkApi = {
  listUsers: async (role_id) =>
    (await api.get(`/roles/${role_id}/users`)).data,
  bulkAssign: async (role_id, user_ids) =>
    (await api.post(`/roles/${role_id}/users/assign`, { user_ids })).data,
  bulkRemove: async (role_id, user_ids) =>
    (await api.post(`/roles/${role_id}/users/unassign`, { user_ids })).data,
};

// ========== Phase 8 — Customer PO ==========
export const customerPoApi = {
  reopen: async (id) => (await api.post(`/customer-po/${id}/reopen`)).data,
  list: async (params = {}) => (await api.get('/customer-po', { params })).data,
  get: async (id) => (await api.get(`/customer-po/${id}`)).data,
  create: async (payload) => (await api.post('/customer-po', payload)).data,
  update: async (id, payload) => (await api.put(`/customer-po/${id}`, payload)).data,
  remove: async (id) => (await api.delete(`/customer-po/${id}`)).data,
  submit: async (id) => (await api.post(`/customer-po/${id}/submit`)).data,
  approve: async (id) => (await api.post(`/customer-po/${id}/approve`)).data,
  reject: async (id, reason) => (await api.post(`/customer-po/${id}/reject`, { reason })).data,
  exportExcelUrl: (id) => `${api.defaults.baseURL}/customer-po/${id}/export-excel`,
  exportPdfUrl: (id) => `${api.defaults.baseURL}/customer-po/${id}/export-pdf`,
};

// ========== Phase 8 — Inquiry Attachments ==========
export const inquiryAttachmentApi = {
  list: async (inquiryId) => (await api.get(`/inquiries/${inquiryId}/attachments`)).data,
  upload: async (inquiryId, file, category) => {
    const fd = new FormData();
    fd.append('file', file);
    if (category) fd.append('category', category);
    const { data } = await api.post(`/inquiries/${inquiryId}/attachments`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  },
  downloadUrl: (attachmentId) => `${api.defaults.baseURL}/inquiries/attachments/${attachmentId}/download`,
  remove: async (attachmentId) => (await api.delete(`/inquiries/attachments/${attachmentId}`)).data,
};

// ========== Phase 8 — Feasibility Departments + Quotation autogen ==========
export const wireShapeConfigApi = {
  list: async (includeInactive = false) =>
    (await api.get(`/wire-shapes${includeInactive ? '?include_inactive=true' : ''}`)).data,
  get: async (code) => (await api.get(`/wire-shapes/${encodeURIComponent(code)}`)).data,
  // Feb 2026 Issue #1 — create endpoint added; drawer now saves new shapes.
  create: async (payload) => (await api.post('/wire-shapes', payload)).data,
  update: async (id, payload) => (await api.put(`/wire-shapes/${id}`, payload)).data,
  saveAxes: async (id, axes) => (await api.put(`/wire-shapes/${id}/axes`, { axes })).data,
  calculate: async (code, dims) =>
    (await api.post(`/wire-shapes/${encodeURIComponent(code)}/calculate`, { dims })).data,
};

export const shapeTransformationApi = {
  list: async (includeInactive = false) =>
    (await api.get(`/shape-transformations${includeInactive ? '?include_inactive=true' : ''}`)).data,
  create: async (payload) => (await api.post('/shape-transformations', payload)).data,
  update: async (id, patch) => (await api.patch(`/shape-transformations/${id}`, patch)).data,
  softDelete: async (id) => (await api.delete(`/shape-transformations/${id}`)).data,
  reduce: async (payload) => (await api.post('/shape-transformations/reduce', payload)).data,
};

// ========== Phase 8 — Feasibility Departments + Quotation autogen ==========
export const feasibilityApi = {
  listWithRollup: async () => (await api.get('/feasibility/list-with-rollup')).data,
  listDepartments: async (feasibilityId) =>
    (await api.get(`/feasibility/${feasibilityId}/departments`)).data,
  updateDepartment: async (feasibilityId, department, payload) =>
    (await api.put(`/feasibility/${feasibilityId}/departments/${department}`, payload)).data,
  // Phase 2e — MKT-FMT-01
  getLines: async (feasibilityId) =>
    (await api.get(`/feasibility/${feasibilityId}/lines`)).data,
  updateLines: async (feasibilityId, lines) =>
    (await api.put(`/feasibility/${feasibilityId}/lines`, { lines })).data,
  patchHeader: async (feasibilityId, patch) =>
    (await api.patch(`/feasibility/${feasibilityId}`, patch)).data,
  getFull: async (feasibilityId) =>
    (await api.get(`/feasibility/${feasibilityId}/full`)).data,
  exportPdfUrl: (feasibilityId) =>
    `${api.defaults.baseURL}/exports/marketing/feasibility/${feasibilityId}/pdf`,
  exportExcelUrl: (feasibilityId) =>
    `${api.defaults.baseURL}/exports/marketing/feasibility/${feasibilityId}/excel`,
};

export const quotationApi = {
  generateFromFeasibility: async (feasibilityId) =>
    (await api.post(`/quotations/generate-from-feasibility/${feasibilityId}`)).data,
  exportExcelUrl: (id) => `${api.defaults.baseURL}/sales/quotations/${id}/export-excel`,
  exportPdfUrl: (id) => `${api.defaults.baseURL}/sales/quotations/${id}/export-pdf`,
  // Batch B1 — one-click conversion of an Approved Quotation to a Draft CPO.
  // Backend: POST /api/customer-po/from-quotation/{quotationId}.  Idempotent:
  // repeat calls return the same CPO with `duplicated: true`.
  convertToCpo: async (quotationId) =>
    (await api.post(`/customer-po/from-quotation/${quotationId}`)).data,
  approve: async (id) => (await api.post(`/sales/quotations/${id}/approve`)).data,
  reject: async (id, reason) => (await api.post(`/sales/quotations/${id}/reject`, { reason })).data,
};



// ========== Phase 3 — PRC (Process Route Card) ==========
export const prcApi = {
  list: async (params = {}) => (await api.get('/production/prc', { params })).data,
  get: async (id) => (await api.get(`/production/prc/${id}`)).data,
  create: async (payload) => (await api.post('/production/prc', payload)).data,
  update: async (id, payload) => (await api.put(`/production/prc/${id}`, payload)).data,
  // Prefill from Sales Order
  prefillFromSO: async (soId) => (await api.get(`/production/prc/from-sales-order/${soId}`)).data,
  searchSalesOrders: async (params = {}) => (await api.get('/production/prc/search-sales-orders', { params })).data,
  // G3.1 — Pre-check whether an SO's lines have unmapped or inactive-PRC FGs.
  checkSoFgMapping: async (soId) => (await api.get(`/production/prc/check-so-fg-mapping/${soId}`)).data,
  // Status transition
  changeStatus: async (id, production_status) => (await api.patch(`/production/prc/${id}/status`, { production_status })).data,
  // Lifecycle-status transition: Draft → Active → Approved (process_route_cards.status)
  activateLifecycleStatus: async (id, status) => (await api.patch(`/production/prc/${id}/lifecycle-status`, { status })).data,
  // Excel export
  // Phase 2a — revision history
  listRevisions: async (id) => (await api.get(`/masters/prc/${id}/revisions`)).data,
  getRevision: async (id, revNo) => (await api.get(`/masters/prc/${id}/revisions/${revNo}`)).data,
};

// ========== Phase 1 (Aug 2026) — PRC-per-FG planning runs ==========
export const prcPlanningApi = {
  // Master lookup: does this FG have a PRC master yet?
  getPrcByFg: async (fgId) => (await api.get(`/production/prc/by-fg/${fgId}`)).data,
  // Planning runs — per PRC
  listRuns: async (prcId, params = {}) => (await api.get(`/production/prc/${prcId}/planning-runs`, { params })).data,
  createRun: async (prcId, payload) => (await api.post(`/production/prc/${prcId}/planning-runs`, payload)).data,
  updateRun: async (prcId, runId, payload) => (await api.patch(`/production/prc/${prcId}/planning-runs/${runId}`, payload)).data,
  deleteRun: async (prcId, runId) => { await api.delete(`/production/prc/${prcId}/planning-runs/${runId}`); return { success: true }; },
  // Fetch a single run + its prc_id / fg_id (used by the edit page when
  // the URL does not carry those as query params — new URL shape).
  getRun: async (runId) => (await api.get(`/production/planning-runs/${runId}`)).data,
  // Cross-PRC list (for the Production→Planning list view)
  listAllRuns: async (params = {}) => (await api.get('/production/planning-runs', { params })).data,
  // SO picker sourced by customer+fg
  salesOrdersByCustomer: async (customerId, params = {}) =>
    (await api.get(`/sales-orders/by-customer/${customerId}`, { params })).data,
  // Audit
  auditDuplicates: async () => (await api.get('/production/prc/audit/duplicates')).data,
};

// ========== Batch B3 — Costing (MKT-FMT-07) ==========
export const costingApi = {
  list: async (params = {}) => (await api.get('/costing/cost-sheets', { params })).data,
  get: async (id) => (await api.get(`/costing/cost-sheets/${id}`)).data,
  create: async (payload) => (await api.post('/costing/cost-sheets', payload)).data,
  update: async (id, payload) => (await api.put(`/costing/cost-sheets/${id}`, payload)).data,
  submit: async (id) => (await api.post(`/costing/cost-sheets/${id}/submit`)).data,
  approve: async (id) => (await api.post(`/costing/cost-sheets/${id}/approve`)).data,
  reject: async (id, remarks) => (await api.post(`/costing/cost-sheets/${id}/reject`, { remarks })).data,
  reopen: async (id) => (await api.post(`/costing/cost-sheets/${id}/reopen`)).data,
  delete: async (id) => (await api.delete(`/costing/cost-sheets/${id}`)).data,
  exportExcelUrl: (id) => `${api.defaults.baseURL}/exports/marketing/costing/${id}/excel`,
  exportPdfUrl: (id) => `${api.defaults.baseURL}/exports/marketing/costing/${id}/pdf`,
};


// ========== Batch B4 — Payments (MKT-RGT-04) ==========
export const paymentApi = {
  dashboard: async () => (await api.get('/payments/dashboard')).data,
  list: async (params = {}) => (await api.get('/payments/collections', { params })).data,
  byInquiry: async (inquiryId) =>
    (await api.get(`/payments/collections/by-inquiry/${inquiryId}`)).data,
  get: async (id) => (await api.get(`/payments/collections/${id}`)).data,
  history: async (id) => (await api.get(`/payments/collections/${id}/history`)).data,
  create: async (payload) => (await api.post('/payments/collections', payload)).data,
  update: async (id, payload) => (await api.put(`/payments/collections/${id}`, payload)).data,
  recordPayment: async (id, payload) =>
    (await api.post(`/payments/collections/${id}/record-payment`, payload)).data,
  writeOff: async (id, reason) =>
    (await api.post(`/payments/collections/${id}/write-off`, { reason })).data,
  delete: async (id) => (await api.delete(`/payments/collections/${id}`)).data,
  // Register exports live on the marketing-exports router; frontend hits
  // those URLs directly with filter passthrough.
  exportExcelUrl: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return `${api.defaults.baseURL}/exports/marketing/payment-collection/excel${qs ? '?' + qs : ''}`;
  },
  exportPdfUrl: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return `${api.defaults.baseURL}/exports/marketing/payment-collection/pdf${qs ? '?' + qs : ''}`;
  },
};

